import React, { useEffect, useState } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import classes from './FilterAccordion.module.scss';

import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useDispatch } from 'react-redux';

import { colorMap, colorGroupRepresentatives, getColorGroup, toHalfWidth } from '../../helper/utils';
import { setDay, setSites } from '../../store/reducers/scheduleReducer';
import { EXCLUDED_MAKERS, FALLBACK_YEAR_RANGE } from '../../config/constants';

const minDistance = 1;

// Chip and section labels, keyed by the filter type the chips carry.
const FILTER_LABELS = {
  maker_name: 'Make',
  model: 'Model',
  grade: 'Grade',
  year: 'Year',
  color: 'Color',
  transmission: 'Trans.',
  bidDate: 'Bid date',
};

const FilterAccordion = ({ items, filterString , setFilterString, reloadCarList, carFiltersList, selectedSavedSearchFilter, setSelectedSavedSearchFilter, scheduleDay, searchText = '', onClearSearch, savedSearchesSlot }) => {
  // eslint-disable-next-line no-unused-vars
  const [selectedFilter, setSelectedFilter] = useState({});
  const [totalFilters, setTotalFilters] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [yearOptions , setYearOptions] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [yearValue, setYearValue] = useState([0, 0]);
  const [availableOptions, setAvailableOptions] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    let newItems = items.map((item) => {
      if (carFiltersList[item.key]) {
        if(item.key === "makerDetail.type" || item.key === "model"){
          console.log("Processing model options for key:", item.key);
          let modelOptions = [];
          
          // Check if there's a Make filter applied
          const selectedMakes = appliedFilters?.maker_name || [];
          
          // If Make is selected, filter models for those makes
          if (selectedMakes.length > 0) {
            console.log("Selected makes:", selectedMakes);
            
            // Try different ways the API might return model data
            if (carFiltersList.makerModels) {
              console.log("Using makerModels from API:", Object.keys(carFiltersList.makerModels));
              
              selectedMakes.forEach(make => {
                if (carFiltersList.makerModels[make]) {
                  console.log(`Found models for ${make}:`, carFiltersList.makerModels[make]);
                  carFiltersList.makerModels[make].forEach(model => {
                    if (model) {
                      let hasChiseseCharacter = false;
                      let modelName = model;
                      if(model.includes("カイ")){
                        modelName = model.replace("カイ", "");
                        hasChiseseCharacter = true;
                      }
                      modelOptions.push({
                        name: modelName,
                        hasChiseseCharacter: hasChiseseCharacter
                      });
                    }
                  });
                }
              });
            } else if (carFiltersList.model) {
              // Fallback to using the full model list if no maker-model mapping is available
              console.log("Using full model list from API");
              
              carFiltersList.model.forEach((model) => {
                if (model) {
                  let hasChiseseCharacter = false;
                  let modelName = model;
                  if(model.includes("カイ")){
                    modelName = model.replace("カイ", "");
                    hasChiseseCharacter = true;
                  }
                  modelOptions.push({
                    name: modelName,
                    hasChiseseCharacter: hasChiseseCharacter
                  });
                }
              });
            } else if (carFiltersList["makerDetail.type"]) {
              // Try using makerDetail.type as another fallback
              console.log("Using makerDetail.type from API");
              
              carFiltersList["makerDetail.type"].forEach((model) => {
                if (model) {
                  let hasChiseseCharacter = false;
                  let modelName = model;
                  if(model.includes("カイ")){
                    modelName = model.replace("カイ", "");
                    hasChiseseCharacter = true;
                  }
                  modelOptions.push({
                    name: modelName,
                    hasChiseseCharacter: hasChiseseCharacter
                  });
                }
              });
            }
          } else {
            // No Make selected, use the default model list
            const modelList = carFiltersList[item.key] || carFiltersList.model || [];
            console.log("Using default model list:", modelList);
            
            modelList.forEach((model) => {
              if (model) {
                let hasChiseseCharacter = false;
                let modelName = model;
                if(model.includes("カイ")){
                  modelName = model.replace("カイ", "");
                  hasChiseseCharacter = true;
                }
                modelOptions.push({
                  name: modelName,
                  hasChiseseCharacter: hasChiseseCharacter
                });
              }
            });
          }
          
          //if modelOptions has duplicate names then make it one
          let unique = {};
          modelOptions = modelOptions.filter(obj => !unique[obj.name] && (unique[obj.name] = true));
          modelOptions.sort((a, b) => a.name.localeCompare(b.name));
          item.options = modelOptions;
          console.log("Final processed model options:", modelOptions);
        } else if(item.key === "maker_name"){
          // Organize makes by country/region
          const makesByCountry = {
            "Japan": ["DAIHATSU", "EUNOS", "HINO", "HONDA", "ISUZU", "MAZDA", "MITSUBISHI", "MITSUOKA", "NISSAN", "SUBARU", "SUZUKI", "TOYOTA"],
            "America (USA)": ["AMERICA NISSAN", "CADILLAC", "CHEVROLET", "CHRYSLER JEEP", "DODGE", "FORD", "U.S.A. OTHER"],
            "Germany": ["AMG", "AUDI", "BMW", "DAIMLER", "MERCEDES BENZ", "MINI", "PORSCHE", "VOLKSWAGEN"],
            "United Kingdom (UK)": ["BENTLEY", "JAGUAR", "LAND ROVER", "ROLLS-ROYCE", "ROVER"],
            "France": ["CITROEN", "RENAULT"],
            "Italy": ["FERRARI", "LAMBORGHINI"],
            "Sweden": ["SAAB", "VOLVO"],
            "Russia": ["LADA"],
            "Miscellaneous / Other Categories": ["BIKE TRIKE"]
          };
            
          // Filter out excluded makes
          const filteredMakes = carFiltersList[item.key].filter(
            (make) => make !== "BMW ALPINA" && make !== "NISSAN DIESEL" && 
                      make !== "OVERSEAS NISSAN" && make !== "MITSUBISHI FUSO" &&
                      !EXCLUDED_MAKERS.includes(make)
          );
          
          // Create grouped options with country headers
          item.options = [];
          
          // Process each country group
          Object.entries(makesByCountry).forEach(([country, makes]) => {
            // Add country header
            item.options.push({ isCountryHeader: true, name: country });
            
            // Add makes that exist in our filtered list
            makes.forEach(make => {
              if (filteredMakes.includes(make)) {
                item.options.push(make);
              }
            });
          });
          
          // Add any remaining makes that weren't in our predefined lists
          const definedMakes = Object.values(makesByCountry).flat();
          const remainingMakes = filteredMakes.filter(make => !definedMakes.includes(make));
          
          if (remainingMakes.length > 0) {
            item.options.push({ isCountryHeader: true, name: "Other" });
            remainingMakes.sort().forEach(make => {
              item.options.push(make);
            });
          }
        }
        else if(item.key === "transmission"){
          item.options = ["Automatic", "Manual"];
        }
        else if(item.key === "color"){
          // Use the getColorGroup function to group similar colors
          const groupedColors = {};
          
          // Group all colors by their color group
          carFiltersList[item.key].forEach(color => {
            if (color) {
              const group = getColorGroup(color);
              if (!groupedColors[group]) {
                groupedColors[group] = [];
              }
              groupedColors[group].push(color);
            }
          });
          
          // Use only the group names as options for the filter
          item.options = Object.keys(groupedColors).sort();
        }
          else{
          item.options = carFiltersList[item.key].sort();
        }
      } else {
        // Check if we should use an alternate key from item properties
        if (item.key === "model" && item.modelKey && carFiltersList[item.modelKey]) {
          console.log("Using alternate model key:", item.modelKey);
          let modelOptions = [];
          carFiltersList[item.modelKey].forEach((model) => {
            if (model) { // Ensure the model is not null or undefined
              let hasChiseseCharacter = false;
              let modelName = model;
              if(model.includes("カイ")){
                modelName = model.replace("カイ", "");
                hasChiseseCharacter = true;
              }
              let modelObject = {
                name: modelName,
                hasChiseseCharacter: hasChiseseCharacter
              }
              modelOptions.push(modelObject);
            }
          });
          //if modelOptions has duplicate names then make it one
          let unique = {};
          modelOptions = modelOptions.filter(obj => !unique[obj.name] && (unique[obj.name] = true));
          modelOptions.sort((a, b) => a.name.localeCompare(b.name));
          item.options = modelOptions;
          console.log("Processed model options from alternate key:", modelOptions);
        } else {
          // Ensure item.options is always initialized
          if (item.key === "model") {
            item.options = [];
          }
        }
      }
      return item;
    }
    );

    setAvailableOptions(newItems);
  }, [items, carFiltersList, filterString, appliedFilters]);

  useEffect(() => {
    if (carFiltersList) {
      console.log('carFiltersList:', carFiltersList);
      
      // Extract year data from the appropriate location in carFiltersList
      let yearFilters = [];
      if (carFiltersList?.carFilter?.year) {
        yearFilters = carFiltersList.carFilter.year;
      } else if (carFiltersList?.year) {
        yearFilters = carFiltersList.year;
      }
      
      console.log('Found year filters:', yearFilters);
      
      if (yearFilters && yearFilters.length > 0) {
        const years = yearFilters.map((year) => Number(year));
        const firstYear = Math.min(...years);
        const lastYear = Math.max(...years);
        
        console.log('Year range:', firstYear, 'to', lastYear);
        
        // Create an array of years between min and max
        const yearsArray = [];
        for (let i = firstYear; i <= lastYear; i++) {
          yearsArray.push(i);
        }
        
        console.log('Generated years array:', yearsArray);
        console.log('Year array length:', yearsArray.length);
        console.log('Last year in array:', yearsArray[yearsArray.length - 1]);
        setYearOptions(yearsArray);
        
        // Only initialize if not already set
        if (yearValue[0] === 0 && yearValue[1] === 0) {
          const maxYear = yearsArray[yearsArray.length - 1];
          console.log('Setting initial year values:', [firstYear, maxYear]);
          setYearValue([firstYear, maxYear]);
        }
      } else {
        // Fallback to a reasonable range if no years are provided
        console.log('No year data found, using fallback years');
        const fallbackYears = [];
        for (let i = FALLBACK_YEAR_RANGE[0]; i <= FALLBACK_YEAR_RANGE[1]; i++) {
          fallbackYears.push(i);
        }
        console.log('Fallback years array:', fallbackYears);
        console.log('Fallback years length:', fallbackYears.length);
        console.log('Last fallback year:', fallbackYears[fallbackYears.length - 1]);
        setYearOptions(fallbackYears);
        
        // Only initialize if not already set
        if (yearValue[0] === 0 && yearValue[1] === 0) {
          console.log('Setting initial fallback year values:', FALLBACK_YEAR_RANGE);
          setYearValue([...FALLBACK_YEAR_RANGE]);
        }
      }
    }
  }, [carFiltersList]); // Removed yearValue from dependencies

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const handleYearChange = (event, newValue, activeThumb) => {
    if (!Array.isArray(newValue)) {
        return;
    }
      const [lower, upper] = newValue;
      //apply min distance
      if (upper - lower < minDistance) {
        if (activeThumb === 0) {
          setYearValue([lower, lower + minDistance]);
        }
        if (activeThumb === 1) {
          setYearValue([upper - minDistance, upper]);
        }
      }else{
        setYearValue(newValue);
      }
      setAppliedFilters({
        ...appliedFilters,
        year: [lower, upper],
      });

};

  const handleFilterSelect = (category, checked, filter, optionArray) => {
    // Skip if this is a country header, not a selectable option
    if (filter && typeof filter === 'object' && filter.isCountryHeader) {
      return;
    }
    
    console.log("Filter select:", category, checked, filter);
    
    let addChineseCharacter = false;
    let actualCategory = category;
    
    // If optionArray has hasChiseseCharacter property, it's a model object
    if (optionArray && typeof optionArray === 'object' && 'hasChiseseCharacter' in optionArray) {
      addChineseCharacter = optionArray.hasChiseseCharacter;
    }
    
    let filterToModify = JSON.parse(JSON.stringify(appliedFilters));
    
    // Special handling for color groups
    if (actualCategory === "color") {
      // When a color group is selected/deselected, handle all colors in that group
      const allColorsInGroup = carFiltersList?.color?.filter(color => getColorGroup(color) === filter) || [];
      
      if (!checked) {
        // Remove all colors in this group from the applied filters
        if (filterToModify?.[actualCategory]) {
          filterToModify[actualCategory] = filterToModify[actualCategory].filter(
            existingFilter => !allColorsInGroup.includes(existingFilter)
          );
        }
      } else {
        // Add all colors in this group to the applied filters
        if (!filterToModify[actualCategory]) {
          filterToModify[actualCategory] = [];
        }
        // Add all colors in this group that aren't already in the filter
        allColorsInGroup.forEach(groupColor => {
          if (!filterToModify[actualCategory].includes(groupColor)) {
            filterToModify[actualCategory].push(groupColor);
          }
        });
      }
      
      setAppliedFilters(filterToModify);
      updateFilterString(filterToModify);
      return;
    }
    
    // Handle model filters specifically
    if (actualCategory === "model" || actualCategory === "makerDetail.type") {
      // Find the real key to use for storing the filter
      const modelItem = items.find(item => item.label === "Model");
      const modelKey = modelItem?.key || "model";
      
      console.log("Model filter selected:", filter, "using key:", modelKey);
      
      // Get the base model name (without "カイ")
      const baseModelName = typeof filter === 'object' ? filter.name : filter;
      // Create the model name with "カイ" suffix
      const modelWithKai = baseModelName + "カイ";
      
      // Check if the "カイ" variant exists in the original model list
      const modelOptions = carFiltersList["model"] || carFiltersList["makerDetail.type"] || [];
      const kaiVariantExists = modelOptions.some(model => model === modelWithKai);
      
      if (!checked) {
        // Remove model
        if (!filterToModify[modelKey]) {
          filterToModify[modelKey] = [];
        }
        
        const index = filterToModify[modelKey].indexOf(baseModelName);
        if (index > -1) {
          filterToModify[modelKey].splice(index, 1);
          
          // Also remove version with Japanese characters if needed
          if (addChineseCharacter || kaiVariantExists) {
            const chineseIndex = filterToModify[modelKey].indexOf(modelWithKai);
            if (chineseIndex > -1) {
              filterToModify[modelKey].splice(chineseIndex, 1);
            }
          }
        }
      } else {
        // Add model
        if (!filterToModify[modelKey]) {
          filterToModify[modelKey] = [];
        }
        
        filterToModify[modelKey].push(baseModelName);
        
        // Add version with Japanese characters if needed
        if ((addChineseCharacter || kaiVariantExists) && !filterToModify[modelKey].includes(modelWithKai)) {
          filterToModify[modelKey].push(modelWithKai);
        }
      }
      
      setAppliedFilters(filterToModify);
      updateFilterString(filterToModify);
      return;
    }
    
    // Original code continues for non-color, non-model filters
    if (!checked) {
      if (!filterToModify[actualCategory]) {
        // Initialize the array if it doesn't exist
        filterToModify[actualCategory] = [];
      }
      
      const index = filterToModify?.[actualCategory]?.indexOf(filter);
      if (index > -1) {
        filterToModify?.[actualCategory].splice(index, 1);
        if (actualCategory === "maker_name" && filter === "BMW") {
          let bmwFilter = filter + " ALPINA";
          const bmwIndex = filterToModify?.[actualCategory].indexOf(bmwFilter);
          if (bmwIndex > -1) {
            filterToModify?.[actualCategory].splice(bmwIndex, 1);
          }
        }
        if (addChineseCharacter) {
          const chineseFilter = filter + "カイ";
          const chineseIndex = filterToModify?.[actualCategory].indexOf(chineseFilter);
          if (chineseIndex > -1) {
            filterToModify?.[actualCategory].splice(chineseIndex, 1);
          }
        }
      }
    } else {
      if (filterToModify?.[actualCategory]?.length > 0) {
        filterToModify?.[actualCategory].push(filter);
        if (actualCategory === "maker_name" && filter === "BMW") {
          filterToModify?.[actualCategory].push("BMW ALPINA");
        }
        if(actualCategory === "maker_name" && filter === "NISSAN"){
          filterToModify?.[actualCategory].push("NISSAN DIESEL");
          filterToModify?.[actualCategory].push("OVERSEAS NISSAN");
        }
        if(actualCategory === "maker_name" && filter === "MITSUBISHI"){
          filterToModify?.[actualCategory].push("MITSUBISHI FUSO");
        }
        if (addChineseCharacter) {
          const chineseFilter = filter + "カイ";
          filterToModify?.[actualCategory].push(chineseFilter);
        }
      } else {
        filterToModify = {
          ...filterToModify,
          [actualCategory]: [filter],
        };
        if (actualCategory === "maker_name" && filter === "BMW") {
          filterToModify?.[actualCategory].push("BMW ALPINA");
        }
        if(actualCategory === "maker_name" && filter === "NISSAN"){
          filterToModify?.[actualCategory].push("NISSAN DIESEL");
          filterToModify?.[actualCategory].push("OVERSEAS NISSAN");
        }
        if(actualCategory === "maker_name" && filter === "MITSUBISHI"){
          filterToModify?.[actualCategory].push("MITSUBISHI FUSO");
        }
        if (addChineseCharacter) {
          const chineseFilter = filter + "カイ";
          filterToModify?.[actualCategory].push(chineseFilter);
        }
      }
    }
    setAppliedFilters(filterToModify);
    updateFilterString(filterToModify);
    setSelectedSavedSearchFilter({name:'',filter:''});
  };

  useEffect(() => {
    let totalFilters = [];
    Object.keys(appliedFilters).forEach((filterType) => {
      if (appliedFilters?.[filterType]?.length > 0) {
        
        // Display model filters correctly in the UI
        const displayType = filterType === "makerDetail.type" ? "model" : filterType;
        
        if (filterType === "year") {
          totalFilters.push({
            type: displayType,
            value: `${appliedFilters?.[filterType][0]} - ${
              appliedFilters?.[filterType][1]
            }`,
          });
        } else if (filterType === "color") {
          // Group colors by their color group
          const colorGroups = {};
          
          appliedFilters?.[filterType].forEach((color) => {
            const group = getColorGroup(color);
            if (!colorGroups[group]) {
              colorGroups[group] = [];
            }
            colorGroups[group].push(color);
          });
          
          // Add one filter entry per color group
          Object.keys(colorGroups).forEach(group => {
            totalFilters.push({
              type: displayType,
              value: group,
              // Store all colors in this group for reference
              colors: colorGroups[group]
            });
          });
        } else {
          appliedFilters?.[filterType].forEach((value) => {
            // Skip duplicate entries for special cases like BMW ALPINA
            if (
              (filterType === "maker_name" && 
                ((value === "BMW ALPINA" && appliedFilters?.maker_name.includes("BMW")) ||
                 (value === "NISSAN DIESEL" && appliedFilters?.maker_name.includes("NISSAN")) ||
                 (value === "OVERSEAS NISSAN" && appliedFilters?.maker_name.includes("NISSAN")) ||
                 (value === "MITSUBISHI FUSO" && appliedFilters?.maker_name.includes("MITSUBISHI")))) ||
              ((filterType === "makerDetail.type" || filterType === "model") && value.includes("カイ") &&
                appliedFilters?.[filterType].includes(value.replace("カイ", "")))
            ) {
              return;
            }

            totalFilters.push({
              type: displayType,
              value: value,
            });
          });
        }
      }
    });
    setTotalFilters(totalFilters);
    updateFilterString(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    let filterArray = filterString.split("&filter");
    let apf = {};
    filterArray.forEach((fd) => {
      if (fd !== "") {
        let kv = fd.split("=");
        let kvKey = kv[0].replace("[", "").replace("]", "");
        let kvValue = JSON.parse(kv[1]);

        apf[kvKey] = kvValue;
        if (kvKey === "year") {
            setYearValue([kvValue[0], kvValue[1]]);
        }
      }
    });
    setAppliedFilters(apf);
  }, [filterString]);

  const handleSelectChange = (event, index) => {
    const newValue = parseInt(event.target.value, 10);
    console.log(`Changing year at index ${index} to:`, newValue);
    
    setYearValue(prevValue => {
      const newYearValue = [...prevValue];
      newYearValue[index] = newValue;
      
      // Ensure the values make sense (start ≤ end)
      if (index === 0 && newYearValue[0] > newYearValue[1]) {
        newYearValue[1] = newYearValue[0];
      } else if (index === 1 && newYearValue[1] < newYearValue[0]) {
        newYearValue[0] = newYearValue[1];
      }
      
      console.log('New year value after change:', newYearValue);
      
      // Also update the applied filters
      setAppliedFilters(prevFilters => ({
        ...prevFilters,
        year: newYearValue
      }));
      
      return newYearValue;
    });
  };

  const handleRemoveFilter = (category, value) => {
    // Convert model category if needed
    const actualCategory = category === "model" ? 
      (items.find(item => item.label === "Model")?.key || "model") : 
      category;

    console.log("Removing filter:", category, value, "actual category:", actualCategory);

    let filterToModify = JSON.parse(JSON.stringify(appliedFilters));
    if (actualCategory === "year") {
      filterToModify = {
        ...filterToModify,
        [actualCategory]: [],
      };
      const maxYear = yearOptions.length > 0 ? yearOptions[yearOptions.length - 1] : FALLBACK_YEAR_RANGE[1];
      const minYear = yearOptions.length > 0 ? yearOptions[0] : FALLBACK_YEAR_RANGE[0];
      
      console.log(`Resetting year range to ${minYear}-${maxYear}`);
      setYearValue([minYear, maxYear]);
    } else if (actualCategory === "color") {
      // For colors, since we group them by color group, remove all colors in the selected group
      if (Array.isArray(value)) {
        // If value is an array, it contains all colors to remove
        filterToModify[actualCategory] = filterToModify[actualCategory]?.filter(
          color => !value.includes(color)
        ) || [];
      } else {
        // Otherwise, remove all colors in the same group
        const group = getColorGroup(value);
        const allColorsInGroup = carFiltersList?.color?.filter(color => getColorGroup(color) === group) || [];
        
        filterToModify[actualCategory] = filterToModify[actualCategory]?.filter(
          color => !allColorsInGroup.includes(color)
        ) || [];
      }
    } else {
      // Remove single value for other filter types
      if (actualCategory === "maker_name" && value === "BMW") {
        // Also remove BMW ALPINA if BMW is removed
        const bmwAlpinaIndex = filterToModify?.[actualCategory]?.indexOf("BMW ALPINA");
        if (bmwAlpinaIndex > -1) {
          filterToModify[actualCategory].splice(bmwAlpinaIndex, 1);
        }
      }
      
      if (actualCategory === "maker_name" && value === "NISSAN") {
        // Also remove NISSAN DIESEL and OVERSEAS NISSAN if NISSAN is removed
        const nissanDieselIndex = filterToModify?.[actualCategory]?.indexOf("NISSAN DIESEL");
        if (nissanDieselIndex > -1) {
          filterToModify[actualCategory].splice(nissanDieselIndex, 1);
        }
        
        const overseasNissanIndex = filterToModify?.[actualCategory]?.indexOf("OVERSEAS NISSAN");
        if (overseasNissanIndex > -1) {
          filterToModify[actualCategory].splice(overseasNissanIndex, 1);
        }
      }
      
      if (actualCategory === "maker_name" && value === "MITSUBISHI") {
        // Also remove MITSUBISHI FUSO if MITSUBISHI is removed
        const mitsubishiFusoIndex = filterToModify?.[actualCategory]?.indexOf("MITSUBISHI FUSO");
        if (mitsubishiFusoIndex > -1) {
          filterToModify[actualCategory].splice(mitsubishiFusoIndex, 1);
        }
      }

      // Handle normal value removal
      const filteredValues = filterToModify[actualCategory]?.filter(
        (val) => val !== value
      );
      
      // Also check for and remove Chinese character version if it exists
      if (actualCategory === "model") {
        const chineseValue = value + "カイ";
        const filteredWithoutChinese = filteredValues?.filter(
          (val) => val !== chineseValue
        );
        filterToModify[actualCategory] = filteredWithoutChinese || [];
      } else {
        filterToModify[actualCategory] = filteredValues || [];
      }
      
      // If this was the last filter of this type, clean up by removing the empty array
      if (filterToModify[actualCategory]?.length === 0) {
        delete filterToModify[actualCategory];
      }
    }
    setAppliedFilters(filterToModify);
    if(scheduleDay){
      dispatch(setDay(''));
      dispatch(setSites([]));
    }
    
    // Update the filter string and reset saved search filter
    updateFilterString(filterToModify);
    setSelectedSavedSearchFilter({ name: "", filter: "" });
  };

 //create classNames so that I can access it like this classNames[label].container
  const classNames = {
    AppliedFilters: {
      container: classes.appliedCarFilterContainer,
      option: classes.appliedCarFilterOption
    },
    Make: {
      container: classes.makeCarFilterContainer,
      option: classes.makeCarFilterOption
    },
    Model: {
      container: classes.modelCarFilterContainer,
      option: classes.modelCarFilterOption
    },
    Grade: {
      container: classes.gradeCarFilterContainer,
      option: classes.gradeCarFilterOption
    },
    Year: {
      container: classes.yearCarFilterContainer,
      option: classes.yearCarFilterOption
    },
    Color: {
      container: classes.colorCarFilterContainer,
      option: classes.colorCarFilterOption
    },
    Transmission: {
      container: classes.transmissionCarFilterContainer,
      option: classes.transmissionCarFilterOption
    },
    "Bid Date": {
      container: classes.bidDateCarFilterContainer,
      option: classes.bidDateCarFilterOption
    }
  }

  const renderContent = (content, label) => {
    // Add a debug console log for year options
    if (label === "Year") {
      console.log('Rendering year options:', yearOptions);
      console.log('Current yearValue:', yearValue);
      console.log('yearOptions length:', yearOptions.length);
      console.log('yearOptions first and last:', yearOptions[0], yearOptions[yearOptions.length - 1]);
    }
    
    if (label === "Model") {
      console.log('Rendering model options:', content.find(item => item.label === "Model")?.options);
      console.log('Model key:', content.find(item => item.label === "Model")?.modelKey);
      console.log('Applied filters:', appliedFilters);
      console.log('Is make selected:', appliedFilters?.maker_name && appliedFilters.maker_name.length > 0);
    }
    
    return (
      <>
        {content.map((item, index) => {
          if (item.label === label && item.label === "Model") {
            // Get the appropriate key for model filtering
            // Use item.key ("model") so the render's active-state check and toggle
            // read the SAME key that handleFilterSelect / updateFilterString write to.
            const modelKey = item.key || item.modelKey;
            console.log(`Model rendering: using key ${modelKey}`);
            
            // Check if any Make is selected
            const isMakeSelected = appliedFilters?.maker_name && appliedFilters.maker_name.length > 0;
            console.log(`Model rendering: isMakeSelected = ${isMakeSelected}, options length = ${item.options?.length || 0}`);
            
            return (
              <div key={index} className={classNames[item.label].container} style={{ paddingBottom: '24px' }}>
                {isMakeSelected ? 
                  ((item.options && item.options.length > 0) ? 
                    item.options.map((option, optionIndex) => (
                      <div 
                        key={optionIndex} 
                        className={`${classNames[item.label].option} ${appliedFilters?.[modelKey]?.includes(option.name) ? classes.activeFilter : ''}`} 
                        onClick={() => handleFilterSelect(modelKey, appliedFilters?.[modelKey]?.includes(option.name) ? false : true, option.name, option)}
                      >
                        <label htmlFor={option.name}>{option.name}</label>
                      </div>
                    )) 
                  : <div className={classes.noOptionsMessage}>No models available for the selected Make. Please try a different Make.</div>
                  )
                  : <div className={classes.noOptionsMessage}>Please select a Make to display available models</div>
                }
              </div>
            );
          }
          else if (item.label === label) {
            return (
              <>
                {item.label === label && <div className={classNames[item.label].container}>
                  {item.label !== "Model" && item.options && item.options.map((option, optionIndex) => {
                    // Handle country headers for Make filter
                    if (item.key === "maker_name" && option && typeof option === 'object' && option.isCountryHeader) {
                      return (
                        <div key={optionIndex} className={classes.countryHeader}>
                          {option.name}
                        </div>
                      );
                    }
                    
                    // Handle regular filter option
                    return (
                      <div 
                        key={optionIndex} 
                        className={`${
                          item.label === "Color" ? classes.colorCarFilterOption : classNames[item.label].option
                        } ${
                          appliedFilters?.[item.key]?.includes(option) ? classes.activeFilter : ''
                        }`} 
                        style={{background: item.label === "Color" ? colorGroupRepresentatives[option] || colorMap[option] : "" }} 
                        onClick={() => handleFilterSelect(
                          item.key, 
                          appliedFilters?.[item.key]?.includes(option) 
                            ? false
                            : true,
                          option
                        )}
                        data-tooltip={item.label === "Color" ? option : null}
                        aria-label={item.label === "Color" ? `Select ${option} color` : null}
                      >
                        {item.label !== "Color" && item.label !== "Year" && <label htmlFor={option}>{option}{item.label === "Bid Date" && " JST" }</label>}
                      </div>
                    );
                  })}
                  {item.label === "Year" && (
                    <div className={classNames[item.label].option}>
                      <div className={classes.yearDropDownContainer}>   
                        <Select
                          value={yearValue[0]}
                          onChange={(e) => handleSelectChange(e, 0)}
                          displayEmpty
                          sx={{
                            width: '40%',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#7F56D9',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#7F56D9',
                            },
                            '& .MuiSelect-select': {
                              padding: '8px 14px',
                              fontSize: '14px',
                              fontFamily: 'Inter, sans-serif',
                            }
                          }} 
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                                backgroundColor: '#fff',
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px'
                              },
                            },
                          }}
                        >
                          {Array.isArray(yearOptions) && yearOptions.length > 0 ? (
                            yearOptions.map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))
                          ) : (
                            // Fallback options
                            Array.from({ length: 38 }, (_, i) => 1990 + i).map(year => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        
                        <div className={classes.yearDivider}/>
                        
                        <Select
                          value={yearValue[1]}
                          onChange={(e) => handleSelectChange(e, 1)}
                          displayEmpty
                          sx={{
                            width: '40%',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#7F56D9',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#7F56D9',
                            },
                            '& .MuiSelect-select': {
                              padding: '8px 14px',
                              fontSize: '14px',
                              fontFamily: 'Inter, sans-serif',
                            }
                          }} 
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                                backgroundColor: '#fff',
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px'
                              },
                            },
                          }}
                        >
                          {Array.isArray(yearOptions) && yearOptions.length > 0 ? (
                            // Map the year options to menu items, and ensure 2003 is included
                            yearOptions.map((year) => {
                              console.log(`Rendering year option: ${year}`);
                              return (
                                <MenuItem key={year} value={year}>
                                  {year}
                                </MenuItem>
                              );
                            })
                          ) : (
                            // Fallback options
                            Array.from({ length: 38 }, (_, i) => 1990 + i).map(year => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </div>
                      <Box sx={{ width: '85%', justifyContent: 'center', margin: 'auto' }}>
                        <Slider
                          getAriaLabel={() => 'Year range'}
                          value={yearValue}
                          min={yearOptions.length > 0 ? yearOptions[0] : FALLBACK_YEAR_RANGE[0]}
                          max={yearOptions.length > 0 ? yearOptions[yearOptions.length - 1] : FALLBACK_YEAR_RANGE[1]}
                          onChange={handleYearChange}
                          valueLabelDisplay="auto"
                          getAriaValueText={(value) => `${value}`}
                          disableSwap
                          sx={{
                            color: '#7F56D9',
                            '& .MuiSlider-thumb': {
                              backgroundColor: '#fff', 
                              border: '2px solid #7F56D9',
                            },
                            '& .MuiSlider-rail': {
                              backgroundColor: '#e0e0e0',
                              opacity: 1,
                            },
                            '& .MuiSlider-track': {
                              backgroundColor: '#7F56D9',
                              border: 'none',
                            },
                            '& .MuiSlider-valueLabel': {
                              backgroundColor: '#7F56D9',
                            },
                          }}
                        />
                      </Box>
                    </div>
                  )}
                </div>}
              </>
            );
          }
          
          return null;
        })}
      </>
    );
  };

  // Helper function to update the filter string based on applied filters
  const updateFilterString = (filters) => {
    let newFilterString = "";
    Object.keys(filters).forEach((filterType) => {
      if (filters?.[filterType]?.length > 0) {
        newFilterString += `&filter[${filterType}]=` + JSON.stringify(filters?.[filterType]);
      }
    });
    setFilterString(newFilterString);
  };

  const trimmedSearch = (searchText || '').trim();
  const hasCriteria = totalFilters.length > 0 || trimmedSearch !== '';

  // What each section header shows once something in it is picked: the year range, or
  // how many values are ticked.
  const sectionSummary = (key) => {
    const picked = totalFilters.filter((filter) => filter.type === key);
    if (picked.length === 0) return null;
    return key === 'year' ? picked[0].value : picked.length;
  };

  return (
    <div className={classes.Accordion}>
      <div className={`${classes.AppliedFilterContainer}`}>
      <div className={classes.AppliedFiltersHeading}>
          <h5>Your search</h5>
          {hasCriteria && <button
            type="button"
            className={classes.clearAll}
            onClick={() => {
              setAppliedFilters({});
              setTotalFilters([]);
              if (trimmedSearch && onClearSearch) onClearSearch();
            }}
          >
            Clear all
          </button>
      }
        </div>
      {
        hasCriteria ? (
      <div className={`${classes.AppliedFilters}`}>
        {trimmedSearch && (
          <button type="button" className={classes.AppliedFilter} onClick={onClearSearch} title="Remove this search text">
            <span className={classes.chipLabel}>Search</span>
            <p>{trimmedSearch}</p>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        )}
        {totalFilters.map((filter, index) => (
          <button
            type="button"
            key={index}
            className={classes.AppliedFilter}
            // A color chip stands for its whole color group, so it removes every color in it
            onClick={() => handleRemoveFilter(filter.type, filter.type === "color" && filter.colors ? filter.colors : filter.value)}
            title="Remove this filter"
          >
            <span className={classes.chipLabel}>{FILTER_LABELS[filter.type] || filter.type}</span>
            <p>{toHalfWidth(filter.value)}</p>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        ))}
      </div>
      ) : (
        <p className={classes.AppliedFiltersEmpty}>
          Showing all cars. Pick a make, model or year below to narrow it down.
        </p>
      )}
      </div>
      {savedSearchesSlot}
      {availableOptions.map((item, index) => (
        <div key={index} className={classes.AccordionItem}>
          <div 
            className={classes.AccordionHeader}
            onClick={() => {
              setSelectedFilter({ key: item.key, value: item.options });
              toggleAccordion(item.label);
            }}
          >
            <div className={classes.AccordionHeaderContent}>
              <i className={`${item.icon}`}/>
              <h5>{item.label}</h5>
              {sectionSummary(item.key) !== null && (
                <span className={classes.sectionCount}>{sectionSummary(item.key)}</span>
              )}
            </div>
            <div className={classes.AccordionHeaderChevron}
              >
              <ChevronDownIcon
                className={`${classes.Icon} ${
                  activeIndex === item.label ? classes.ActiveIcon : ''
              }`} 
              />
            </div>
          </div>
          <div
            className={`${classes.AccordionContent} ${
              activeIndex === item.label ? classes.active : ''
            }`}
          >
            {renderContent(availableOptions, item.label)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FilterAccordion;